'use client';

import clsx from 'clsx';
import React, { ReactElement, useEffect } from 'react';

import { Button } from '@/components/button/button';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { Source } from '@/types/evals';

import styles from './modalSourceShow.module.scss';

type Props = {
  // Backwards compatibility: accept both onCloseAction and onClose
  onCloseAction: () => void;
  source: Source;
};

export default function ModalSourceShow( { onCloseAction, source }: Props ) {
  // Close the modal on Escape press to match common UX expectations
  useEffect( () => {
    const onKeyDown = ( e: KeyboardEvent ) => {
      if ( e.key === 'Escape' ) {
        onCloseAction();
      }
    };

    document.addEventListener( 'keydown', onKeyDown );
    return () => document.removeEventListener( 'keydown', onKeyDown );
  }, [ onCloseAction ] );

  const buildKeyValueFields = ( obj?: object, parentKey = '' ): ReactElement[] => {
    if( !obj ) return [];

    return Object.entries( obj ).flatMap( ( [ key, value ] ) => {
      const fullKey = parentKey ? `${ parentKey }.${ key }` : key;

      if ( typeof value === 'object' && value !== null ) {
        return buildKeyValueFields( value, fullKey );
      }

      const displayValue = value !== undefined && value !== null ? value.toString() : '-';
      const testId = `source-field-${ fullKey.replace( /\./g, '-' ) }`;

      return (
        <div className={ clsx( 'layout__col', styles.config_item ) } key={ `source-key-${ fullKey }` } data-testid={ testId }>
          <fieldset>
            <legend className={ styles.key }>{ fullKey }</legend>
            <p className={ styles.val }>{ displayValue }</p>
          </fieldset>
        </div>
      );
    } );
  };

  return (
    <Modal
      resizable
      modalType="source-details"
      initialSize="large"
      minSize={ { width: 500, height: 300 } }
    >
      <div className={ styles.content } data-testid='source-details-modal'>
        <Heading as="h2" size="md" data-testid='source-name-title'>Source: { source.name }</Heading>
        <div className={ clsx( 'layout__col', styles.doms ) } data-testid='source-details-content'>
          { buildKeyValueFields( source ) }
        </div>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button variant='primary' onClick={ onCloseAction } data-testid='source-details-close-button'>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
